<template>
  <div class="news-page">
    <!-- News Menu Section -->
    <section class="news-menu py-12 bg-primary">
      <v-container class="max-w-container">
        <v-row align="center">
          <v-col cols="12" class="text-center mb-6">
            <h1 class="text-h3 font-weight-bold text-white">{{ $t('news.menuTitle') }}</h1>
          </v-col>
          <v-col cols="12">
            <v-row justify="center">
              <v-col 
                v-for="(menuItem, index) in newsMenuItems" 
                :key="index" 
                cols="12" 
                sm="6" 
                md="3"
                class="d-flex justify-center"
              >
                <v-card 
                  class="news-menu-card elevation-6 text-center"
                  :to="menuItem.link"
                  hover
                >
                  <v-card-title class="justify-center">
                    <v-icon 
                      :icon="menuItem.icon" 
                      size="64" 
                      class="mb-4 text-primary"
                    ></v-icon>
                  </v-card-title>
                  <v-card-text>
                    <div class="text-h6 font-weight-bold mb-2">
                      {{ menuItem.title }}
                    </div>
                    <div class="text-body-2 text-grey">
                      {{ menuItem.description }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Technology Section -->
    <section id="technology" class="technology-section py-12">
      <v-container class="max-w-container">
        <v-row>
          <v-col cols="12">
            <h2 class="text-h4 font-weight-bold mb-6">{{ $t('news.technology.title') }}</h2>
            <p class="text-body-1 mb-6">{{ $t('news.technology.description') }}</p>
            
            <v-row>
              <v-col cols="12" md="6" lg="3" v-for="(tech, index) in technologyAdvantages" :key="index">
                <v-card flat class="technology-card h-100">
                  <v-card-title class="text-h6 font-weight-bold">
                    {{ tech.title }}
                  </v-card-title>
                  <v-card-text class="text-body-2">
                    {{ tech.description }}
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- News Section -->
    <section id="news" class="news-section py-12 bg-grey-lighten-4">
      <v-container class="max-w-container">
        <v-row>
          <v-col cols="12">
            <h2 class="text-h4 font-weight-bold mb-6">{{ $t('news.title') }}</h2>
            
            <!-- News Categories -->
            <div class="d-flex flex-wrap mb-6">
              <v-btn
                v-for="category in categories"
                :key="category.value"
                :variant="selectedCategory === category.value ? 'flat' : 'tonal'"
                :color="selectedCategory === category.value ? 'primary' : undefined"
                :class="[
                  'text-none mr-2 mb-2',
                  selectedCategory === category.value ? 'category-active' : 'category-btn'
                ]"
                @click="selectedCategory = category.value"
                size="large"
              >
                {{ category.label }}
              </v-btn>
            </div>

            <!-- Featured News -->
            <v-row class="mb-6">
              <v-col v-for="news in featuredNews" :key="news.id" cols="12" md="4">
                <NewsCard :news="news" />
              </v-col>
            </v-row>

            <!-- Latest News -->
            <v-row>
              <v-col v-for="news in filteredNews" :key="news.id" cols="12" md="4">
                <NewsCard :news="news" />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- FAQ Section -->
    <section id="faq" class="faq-section py-12">
      <v-container class="max-w-container">
        <v-row>
          <v-col cols="12">
            <h2 class="text-h4 font-weight-bold mb-6">{{ $t('news.faq.title') }}</h2>
            
            <v-expansion-panels>
              <v-expansion-panel v-for="(faq, index) in faqs" :key="index">
                <v-expansion-panel-title>
                  {{ faq.question }}
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  {{ faq.answer }}
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Global MAP Section -->
    <section id="global-map" class="global-map-section py-12 bg-grey-lighten-4">
      <v-container class="max-w-container">
        <v-row>
          <v-col cols="12">
            <h2 class="text-h4 font-weight-bold mb-6">{{ $t('news.globalMap.title') }}</h2>
            
            <div class="global-map-container">
              <v-img 
                src="/public/images/global-market-map.png" 
                alt="Global Market Map" 
                class="global-map-image"
                contain
              >
                <template v-slot:placeholder>
                  <v-layout 
                    fill-height 
                    align-center 
                    justify-center 
                    ma-0
                  >
                    <v-progress-circular 
                      indeterminate 
                      color="primary"
                    ></v-progress-circular>
                  </v-layout>
                </template>
              </v-img>
              
              <div class="global-map-overlay">
                <p class="text-body-1 text-center">
                  {{ $t('news.globalMap.description') }}
                </p>
                <div class="market-stats mt-4">
                  <div class="stat-item">
                    <v-icon color="primary" class="mr-2">mdi-earth</v-icon>
                    <span>{{ $t('news.globalMap.countries') }}: 50+</span>
                  </div>
                  <div class="stat-item">
                    <v-icon color="primary" class="mr-2">mdi-flag-variant</v-icon>
                    <span>{{ $t('news.globalMap.regions') }}: 5</span>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { NewsItem } from '@/types/news';
import NewsCard from '@/components/NewsCard.vue';
import { newsArticles } from '@/data/news';

const { t } = useI18n();

// 新闻动态菜单项
const newsMenuItems = [
  {
    title: t('news.menu.technology'),
    description: t('news.menu.technologyDesc'),
    icon: 'mdi-cog',
    link: '#technology'
  },
  {
    title: t('news.menu.news'),
    description: t('news.menu.newsDesc'),
    icon: 'mdi-newspaper',
    link: '#news'
  },
  {
    title: t('news.menu.faq'),
    description: t('news.menu.faqDesc'),
    icon: 'mdi-help-circle',
    link: '#faq'
  },
  {
    title: t('news.menu.globalMap'),
    description: t('news.menu.globalMapDesc'),
    icon: 'mdi-earth',
    link: '#global-map'
  }
];

// 技术优势
const technologyAdvantages = [
  {
    title: t('news.technology.advantage1.title'),
    description: t('news.technology.advantage1.description')
  },
  {
    title: t('news.technology.advantage2.title'),
    description: t('news.technology.advantage2.description')
  },
  {
    title: t('news.technology.advantage3.title'),
    description: t('news.technology.advantage3.description')
  },
  {
    title: t('news.technology.advantage4.title'),
    description: t('news.technology.advantage4.description')
  }
];

// FAQ 列表
const faqs = [
  {
    question: t('news.faq.question1'),
    answer: t('news.faq.answer1')
  },
  {
    question: t('news.faq.question2'),
    answer: t('news.faq.answer2')
  },
  {
    question: t('news.faq.question3'),
    answer: t('news.faq.answer3')
  }
];

// 新闻分类
const categories = [
  { label: t('news.categories.all'), value: 'all' },
  { label: t('news.categories.company'), value: 'company' },
  { label: t('news.categories.industry'), value: 'industry' },
  { label: t('news.categories.product'), value: 'product' }
];

const selectedCategory = ref('all');

// 获取特色新闻（最新的3条）
const featuredNews = computed(() => {
  return [...newsArticles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
});

// 过滤新闻列表（除了特色新闻之外的所有新闻）
const filteredNews = computed(() => {
  const featured = featuredNews.value;
  const otherNews = newsArticles.filter(news => !featured.find(f => f.id === news.id));
  
  if (selectedCategory.value === 'all') {
    return otherNews;
  }
  return otherNews.filter(news => news.type === selectedCategory.value);
});
</script>

<style scoped>
.news-page {
  background: #ffffff;
}

.global-map-container {
  position: relative;
  width: 100%;
  min-height: 500px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.global-map-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.global-map-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255,255,255,0.8);
  padding: 20px;
  text-align: center;
}

.market-stats {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  font-weight: 500;
}

/* 其他样式保持不变 */
.news-menu {
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgba(var(--v-theme-primary), 0.8) 100%);
  clip-path: polygon(0 0, 100% 0, 100% 90%, 0 100%);
}

.news-menu-card {
  transition: all 0.3s ease;
  transform-origin: center;
  max-width: 300px;
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 24px;
}

.news-menu-card:hover {
  transform: scale(1.05);
  box-shadow: 0 16px 32px rgba(0,0,0,0.1) !important;
}

.technology-section,
.news-section,
.faq-section,
.global-map-section {
  position: relative;
}

.technology-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  border-radius: 12px;
}

.technology-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

/* 复用之前的分类按钮样式 */
.category-btn {
  background: white !important;
  border: 1px solid rgba(var(--v-theme-primary), 0.1) !important;
  transition: all 0.3s ease;
}

.category-btn:hover {
  border-color: rgba(var(--v-theme-primary), 0.3) !important;
  background: rgba(var(--v-theme-primary), 0.05) !important;
  transform: translateY(-2px);
}

.category-active {
  font-weight: 500;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.2);
}

/* 响应式调整 */
@media (max-width: 600px) {
  .technology-section .v-col,
  .news-section .v-col {
    padding: 8px;
  }
}
</style>
